import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, ChevronLeft, ChevronRight, Eye, ClipboardCopy, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { 
  Drawer, 
  DrawerContent, 
  DrawerHeader, 
  DrawerTitle, 
  DrawerDescription
} from '@/components/ui/drawer';
import { getTermColorInfo, TermColorInfo } from '@/components/vocabulary/term-card-backgrounds';
import { cn } from '@/lib/utils';
import { createVocabularyTermKey } from '@/lib/vocabulary-term-key';
import { useI18n } from '@/lib/i18n';
import { useUser } from '@/lib/context/user-context';
import { useUserSettings } from '@/contexts/user-settings-context';
import { useWordExplanation } from '@/hooks/use-word-explanation';
import { normalizeChosenExplanationText } from '@/lib/vocabulary/chosen-explanation';

// Mock SpeakerIcon component
const SpeakerIcon = ({ lang, text, size = 16 }: { lang: string; text: string; size?: number }) => (
  <button 
    className="hover:bg-black/5 dark:hover:bg-white/10 text-[#1C1C1E] dark:text-white/80 rounded-full p-1 transition-colors flex items-center justify-center leading-none"
    onClick={(e) => {
      e.stopPropagation();
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = lang === 'en' ? 'en-US' : lang;
        speechSynthesis.speak(utterance);
      }
    }}
  >
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="11 5,6 9,2 9,2 15,6 15,11 19,11 5"/>
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
    </svg>
  </button>
);

const CEFR_LEVELS = new Set(["A1", "A2", "B1", "B2", "C1", "C2"]);

export const normalizeCefrLevel = (rawLevel?: string | null): string | undefined => {
  if (typeof rawLevel !== "string") {
    return undefined;
  }
  const level = rawLevel.trim().toUpperCase();
  return CEFR_LEVELS.has(level) ? level : undefined;
};

interface VocabularyTerm {
  term: string;
  explanation: string;
  chosen_explanation?: string | null;
  chosen_explanation_audio?: string | null;
  timestamp?: number | string;
  endTimestamp?: number | string;
  level?: string | null;
  examples: Array<{
    sentence?: string;
    translation?: string;
    english?: string;
    timestamp?: number | string;
    endTimestamp?: number | string;
    type?: string;
    context_note?: string;
  }>;
  context_description?: string;
}

interface VocabularyScenario {
  name: string;
  terms: VocabularyTerm[];
}

interface VocabularyAnalysis {
  scenarios: VocabularyScenario[];
}

export type ActiveVocabularyTermData = TermColorInfo & {
  term: string;
  scenarioName?: string;
  level?: string;
  // 🎯 用于 hover card 显示的 vocabulary 数据
  explanation?: string | Record<string, string>;
  context_description?: string;
  example?: {
    sentence?: string;
    translation?: string | Record<string, string>;
  };
};

interface ImmersiveVocabularyCardsProps {
  vocabulary: VocabularyAnalysis;
  currentTime: number;
  onTimestampClick?: (timestamp: number, endTimestamp?: number) => void;
  onActiveVocabularyTermsChange?: (terms: ActiveVocabularyTermData[]) => void;
  showTranslation?: boolean;
  revealAllTranslations?: boolean;
}

interface TimelineCard {
  term: VocabularyTerm;
  example: any;
  practicalExample: any;
  timestamp: number;
  endTimestamp?: number;
  scenarioName: string;
  id: number;
  colorIndex: number;
  backgroundClass: string;
  lightBackgroundClass: string;
  darkBackgroundClass?: string;
  textClass: string;
  level?: string;
}

const TranslationReveal = ({
  revealed,
  onReveal,
  children,
  className,
  overlayLabel = 'Double click to reveal',
}: {
  revealed: boolean;
  onReveal: () => void;
  children: React.ReactNode;
  className?: string;
  overlayLabel?: string;
}) => {
  const handleClick = (event: React.MouseEvent | React.TouchEvent) => {
    event.stopPropagation();
  };

  const handleDoubleClick = (event: React.MouseEvent | React.TouchEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!revealed) {
      onReveal();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!revealed && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      event.stopPropagation();
      onReveal();
    }
  };

  return (
    <div
      role={!revealed ? 'button' : undefined}
      tabIndex={!revealed ? 0 : -1}
      onClick={handleClick as any}
      onDoubleClick={handleDoubleClick as any}
      onKeyDown={handleKeyDown}
      className={cn(
        'group relative pl-4 w-full min-w-0 text-left rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 transition select-text',
        revealed ? 'cursor-text' : 'cursor-pointer'
      )}
    >
      <div
        className={cn(
          'w-full min-w-0 transition duration-200 ease-out',
          revealed ? 'blur-0' : 'blur-sm',
          className
        )}
      >
        {children}
      </div>
      {!revealed && (
        <span className="pointer-events-none absolute inset-0 flex items-center pl-4 rounded-xl text-sm font-medium text-muted-foreground/50 dark:text-white/50">
          {overlayLabel}
        </span>
      )}
    </div>
  );
};

const parseTimestampValue = (value: number | string | undefined | null): number | null => {
  if (value === undefined || value === null) {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const hmsFractionPattern = /^\d{1,2}:\d{2}:\d{2}(?:\.\d+)?$/;
  const msFractionPattern = /^\d{1,2}:\d{2}(?:\.\d+)?$/;

  if (hmsFractionPattern.test(trimmed)) {
    const [hoursPart, minutesPart, secondsPart] = trimmed.split(':');
    const hours = Number(hoursPart);
    const minutes = Number(minutesPart);
    const seconds = Number(secondsPart);
    if (
      Number.isFinite(hours) &&
      Number.isFinite(minutes) &&
      Number.isFinite(seconds)
    ) {
      return hours * 3600 + minutes * 60 + seconds;
    }
    return null;
  }

  if (msFractionPattern.test(trimmed)) {
    const [minutesPart, secondsPart] = trimmed.split(':');
    const minutes = Number(minutesPart);
    const seconds = Number(secondsPart);
    if (Number.isFinite(minutes) && Number.isFinite(seconds)) {
      return minutes * 60 + seconds;
    }
    return null;
  }

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const CARD_PREVIEW_LEAD_SECONDS = 5;
const CARD_POST_GRACE_SECONDS = 0.75;

// Card Stack Component - 确保动画与原始CardStack完全一致
const VocabularyCardStack = ({
  items,
  offset = 5,
  scaleFactor = 0.02,
  currentTime,
  onTimestampClick,
  activeCardIndex = -1,
  setSelectedCard,
  setIsDrawerOpen,
  isTranslationRevealed,
  onRevealTranslation,
}: {
  items: TimelineCard[];
  offset?: number;
  scaleFactor?: number;
  currentTime: number;
  onTimestampClick?: (timestamp: number, endTimestamp?: number) => void;
  activeCardIndex?: number;
  setSelectedCard: (card: TimelineCard | null) => void;
  setIsDrawerOpen: (open: boolean) => void;
  isTranslationRevealed: (cardId: number) => boolean;
  onRevealTranslation: (cardId: number) => void;
}) => {
  const CARD_OFFSET = offset;
  const SCALE_FACTOR = scaleFactor;
  const STACK_X_OFFSET = 0;
  const STACK_Y_OFFSET = 32;
  const BASE_STACK_HEIGHT = 70;
  const [cards, setCards] = useState<TimelineCard[]>(items);
  const [currentActiveIndex, setCurrentActiveIndex] = useState(-1);
  const [primaryOverrideId, setPrimaryOverrideId] = useState<number | null>(null);

  const stackedCards = useMemo<TimelineCard[]>(() => {
    if (!items.length) return [];

    const relevantCards = items
      .filter(card => {
        const endTimestamp = card.endTimestamp ?? card.timestamp + CARD_POST_GRACE_SECONDS;
        const previewWindowStart = card.timestamp - CARD_PREVIEW_LEAD_SECONDS;
        // Keep cards that are within the preview window and haven't passed their end time
        return currentTime >= previewWindowStart && currentTime <= endTimestamp + CARD_POST_GRACE_SECONDS;
      })
      .sort((a, b) => {
        const diffA = a.timestamp - currentTime;
        const diffB = b.timestamp - currentTime;

        // Prefer cards that are already active (diff <= 0)
        if ((diffA <= 0) !== (diffB <= 0)) {
          return diffA <= 0 ? -1 : 1;
        }

        const absComparison = Math.abs(diffA) - Math.abs(diffB);
        if (absComparison !== 0) {
          return absComparison;
        }

        return a.timestamp - b.timestamp;
      });

    if (relevantCards.length) {
      return relevantCards.slice(0, 3);
    }

    const nextCard = items.find(card => card.timestamp >= currentTime);
    if (nextCard) {
      return [nextCard];
    }

    return [items[items.length - 1]];
  }, [items, currentTime]);

  useEffect(() => {
    if (primaryOverrideId === null) return;
    if (!stackedCards.some(card => card.id === primaryOverrideId)) {
      setPrimaryOverrideId(null);
    }
  }, [stackedCards, primaryOverrideId]);

  const displayCards = useMemo(() => {
    if (!stackedCards.length) return stackedCards;
    if (primaryOverrideId === null) return stackedCards;

    const reordered = [...stackedCards];
    const selectedIndex = reordered.findIndex(card => card.id === primaryOverrideId);
    if (selectedIndex <= 0) {
      return reordered;
    }

    const [selectedCard] = reordered.splice(selectedIndex, 1);
    reordered.unshift(selectedCard);
    return reordered;
  }, [stackedCards, primaryOverrideId]);

  useEffect(() => {
    setCards(items);
  }, [items]);

  // 当activeCardIndex变化时，自动调整卡片顺序使其显示在顶部
  useEffect(() => {
    if (activeCardIndex >= 0 && activeCardIndex < items.length && activeCardIndex !== currentActiveIndex) {
      setCurrentActiveIndex(activeCardIndex);
      
      // 重新排列卡片，使activeCardIndex的卡片显示在顶部
      const newCards = [...items];
      const activeCard = newCards[activeCardIndex];
      
      // 移除active card并放到开头
      newCards.splice(activeCardIndex, 1);
      newCards.unshift(activeCard);
      
      setCards(newCards);
    }
  }, [activeCardIndex, items, currentActiveIndex]);

  const moveToNext = () => {
    setCards((prevCards: TimelineCard[]) => {
      const newArray = [...prevCards];
      newArray.unshift(newArray.pop()!);
      return newArray;
    });
  };

  const moveToPrevious = () => {
    setCards((prevCards: TimelineCard[]) => {
      const newArray = [...prevCards];
      newArray.push(newArray.shift()!);
      return newArray;
    });
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        moveToPrevious();
      } else if (event.key === "ArrowRight") {
        moveToNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const formatTimestamp = (timestamp: number | string | undefined | null) => {
    if (timestamp === undefined || timestamp === null) return "0:00";
    
    let numTimestamp: number;
    if (typeof timestamp === 'string') {
      if (/^\d{2}:\d{2}:\d{2}$/.test(timestamp)) {
        const [hours, minutes, seconds] = timestamp.split(':').map(Number);
        numTimestamp = hours * 3600 + minutes * 60 + seconds;
      } else if (/^\d{1,2}:\d{2}$/.test(timestamp)) {
        const [minutes, seconds] = timestamp.split(':').map(Number);
        numTimestamp = minutes * 60 + seconds;
      } else {
        numTimestamp = parseFloat(timestamp) || 0;
      }
    } else {
      numTimestamp = timestamp;
    }
    
    const totalSeconds = Math.round(numTimestamp);
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  if (cards.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>No vocabulary cards available</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center">
      {/* Card Stack Container - 保持与原始CardStack相同的动画参数 */}
      <div
        className="relative w-full sm:mb-4 mb-0 px-8"
        style={{
          perspective: "1000px",
          height: `${BASE_STACK_HEIGHT + Math.max(displayCards.length - 1, 0) * STACK_Y_OFFSET}px`,
        }}
      >
        <AnimatePresence initial={false}>
        {displayCards.map((card, index) => {
          const total = displayCards.length;
          const stackIndex = total - index - 1;
          const depthIndex = index;
          const diffSeconds = card.timestamp - currentTime;
          const cardEnd = card.endTimestamp ?? card.timestamp + CARD_POST_GRACE_SECONDS;
          const isActive = currentTime >= card.timestamp && currentTime <= cardEnd;
          const isPast = currentTime > cardEnd;
          const isCountdown = diffSeconds > 0 && diffSeconds <= CARD_PREVIEW_LEAD_SECONDS;
          const shouldUseHighlightColor = !isCountdown && (isActive || isPast);
          const termColorClass = shouldUseHighlightColor ? card.textClass : 'text-[#1C1C1E] dark:text-white';
          const countdownProgress = isCountdown
            ? Math.min(1, Math.max(0, diffSeconds / CARD_PREVIEW_LEAD_SECONDS))
            : 0;
          const translationRevealed = isTranslationRevealed(card.id);
          const maskStyle = depthIndex === 0 ? undefined : {
            WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 52%, transparent 100%)',
            maskImage: 'linear-gradient(to bottom, black 0%, black 52%, transparent 100%)',
          } as React.CSSProperties;
          const subtitle =
            card.scenarioName ||
            card.term.context_description ||
            card.term.examples?.[0]?.type ||
            null;
          
          return (
            <motion.div
              key={card.id}
              className={cn(
                'absolute border border-border dark:border-muted-foreground/15 bg-white/15 backdrop-blur-xl left-2 right-2 top-0 sm:rounded-3xl rounded-[18px] sm:p-4 md:p-2.5 p-2.5 shadow-lg flex flex-col gap-4 cursor-pointer overflow-hidden transition-transform duration-300 text-[#1C1C1E] dark:text-white',
                {
                  '': isActive,
                  'opacity-90': isCountdown && !isActive,
                  'opacity-75': isPast && !isActive,
                }
              )}
              style={{
                transformOrigin: "top left",
                ...maskStyle,
              }}
              initial={{ opacity: 0 }}
              animate={{
                y: stackIndex * STACK_Y_OFFSET,
                x: stackIndex * STACK_X_OFFSET,
                scale: 1 - depthIndex * SCALE_FACTOR,
                zIndex: total - depthIndex,
                rotateX: depthIndex === 0 ? 0 : -3,
                opacity: 1,
              }}
              exit={{ opacity: 0, scale: 0.9, y: stackIndex * STACK_Y_OFFSET, transition: { duration: 0.25 } }}
              whileHover={depthIndex === 0 ? {
                scale: 1.05,
                rotateX: 5,
                transition: { duration: 0.2 }
              } : {}}
              transition={{
                duration: 0.3,
                ease: "easeInOut",
                rotateX: { duration: 0.4, ease: "easeOut" }
              }}
              onClick={() => {
                setPrimaryOverrideId(card.id);
                setSelectedCard(card);
                setIsDrawerOpen(true);
              }}
            >
              {/* Countdown overlay */}
              <div className="absolute inset-0 pointer-events-none">
                <div
                  className={cn(
                    'absolute inset-y-0 left-0 rounded-2xl',
                    card.lightBackgroundClass,
                    card.darkBackgroundClass
                  )}
                  style={{
                    width: `${Math.max(0, Math.min(1, isCountdown ? countdownProgress : 0)) * 100}%`,
                    opacity: isCountdown ? 0.85 : 0,
                    transition: 'width 240ms ease-out, opacity 220ms ease-out',
                    willChange: 'width, opacity',
                  }}
                />
              </div>

              {/* Card Content */}
              <div className="relative z-10 flex-1 flex flex-col sm:gap-3 gap-0 md:gap-1.5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4
                        className={cn(
                          // Increase font size for medium screens and up
                          'font-oldschool-grotesk-compact text-[16px] sm:text-2xl md:text-[21px] leading-tight line-clamp-1 transition-colors duration-200 capitalize',
                          termColorClass
                        )}
                      >
                        {card.term.term}
                      </h4>
                      <SpeakerIcon 
                        lang="en" 
                        text={card.term.term}
                        size={16}
                      />
                    </div>
                    {/* {subtitle && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">
                        {subtitle}
                      </p>
                    )} */}
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onTimestampClick) {
                        onTimestampClick(card.timestamp, card.endTimestamp);
                      }
                    }}
                    className="flex font-sans items-center gap-1 rounded-full bg-black/5 dark:bg-white/5 px-3 py-1 text-[12px] sm:text-[15px] md:text-base font-semibold text-[#1C1C1E] dark:text-white/90 transition hover:bg-black/10 dark:hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/20 dark:focus-visible:ring-white/40"
                  >
                    <Eye className="h-4 w-4" />
                    {formatTimestamp(card.timestamp)}
                  </button>
                </div>

                {/* Explanation */}
                <TranslationReveal
                  revealed={isTranslationRevealed(card.id)}
                  onReveal={() => onRevealTranslation(card.id)}
                  className="text-[13px] sm:text-base md:text-lg text-card-foreground/60 sm:mb-1 mb-0 line-clamp-1"
                  overlayLabel="Double click to reveal translation"
                >
                  {card.term.explanation}
                </TranslationReveal>
              </div>

              {/* Footer - Status */}
              {/* <div className="relative z-10 flex items-center justify-between text-xs">
                <div className={`flex items-center space-x-1 ${
                  isActive ? 'text-primary' : 
                  isPast ? 'text-muted-foreground' : 
                  'text-muted-foreground'
                }`}>
                  <div className={`w-2 h-2 rounded-full ${
                    isActive ? 'bg-primary animate-pulse' : 
                    isPast ? 'bg-muted-foreground' : 
                    'bg-muted-foreground/50'
                  }`} />
                  <span>
                    {isActive ? 'Current' : 
                     isPast ? 'Completed' : 
                     'Upcoming'}
                  </span>
                </div>
                <div className="text-muted-foreground">
                  {isActive ? (
                    <span className="text-red-500 font-bold text-sm">Now</span>
                  ) : card.timestamp > currentTime ? (
                    <>
                      In <span className={`${
                        Math.ceil(card.timestamp - currentTime) <= 3
                          ? 'text-red-500 font-bold text-base'
                          : ''
                      }`}>
                        {Math.ceil(card.timestamp - currentTime)}s
                      </span>
                    </>
                  ) : 
                  `${Math.ceil(Math.max(0, currentTime - cardEnd))}s ago`}
                </div>
              </div> */}
            </motion.div>
          );
        })}
        </AnimatePresence>
      </div>

      {/* Control Buttons - 取消注释，与原始CardStack保持一致 */}
      {/* <div className="flex gap-4">
        <button
          onClick={moveToPrevious}
          className="p-3 rounded-full bg-background border border-border shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
          aria-label="Previous card"
        >
          <ChevronLeft className="w-5 h-5 text-foreground" />
        </button>
        <button
          onClick={moveToNext}
          className="p-3 rounded-full bg-background border border-border shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
          aria-label="Next card"
        >
          <ChevronRight className="w-5 h-5 text-foreground" />
        </button>
      </div> */}
    </div>
  );
};

// 时间戳格式化函数
const formatTimestamp = (timestamp: number | string | undefined | null) => {
  if (timestamp === undefined || timestamp === null) return "0:00";
  
  let numTimestamp: number;
  if (typeof timestamp === 'string') {
    if (/^\d{2}:\d{2}:\d{2}$/.test(timestamp)) {
      const [hours, minutes, seconds] = timestamp.split(':').map(Number);
      numTimestamp = hours * 3600 + minutes * 60 + seconds;
    } else if (/^\d{1,2}:\d{2}$/.test(timestamp)) {
      const [minutes, seconds] = timestamp.split(':').map(Number);
      numTimestamp = minutes * 60 + seconds;
    } else {
      numTimestamp = parseFloat(timestamp) || 0;
    }
  } else {
    numTimestamp = timestamp;
  }
  
  const totalSeconds = Math.round(numTimestamp);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

export function ImmersiveVocabularyCards({ 
  vocabulary, 
  currentTime, 
  onTimestampClick,
  onActiveVocabularyTermsChange,
  showTranslation: _outerShowTranslation = true,
  revealAllTranslations = false
}: ImmersiveVocabularyCardsProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  // const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [revealedCardIds, setRevealedCardIds] = useState<Set<number>>(() => new Set());
  const previousRevealAllRef = useRef(false);

  const isTranslationRevealed = useCallback(
    (cardId: number) => revealAllTranslations || revealedCardIds.has(cardId),
    [revealAllTranslations, revealedCardIds]
  );

  const revealTranslation = useCallback((cardId: number) => {
    setRevealedCardIds((prev) => {
      if (prev.has(cardId)) return prev;
      const next = new Set(prev);
      next.add(cardId);
      return next;
    });
  }, []);

  void _outerShowTranslation;
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const checkMobile = () => {
      const { innerWidth, navigator } = window;
      const userAgent = navigator.userAgent || "";
      const platform = navigator.platform || "";
      const uaDataPlatform =
        (navigator as typeof navigator & { userAgentData?: { platform?: string } })
          ?.userAgentData?.platform || "";
      const platformSignature = `${platform} ${uaDataPlatform}`.toLowerCase();
      const maxTouchPoints = navigator.maxTouchPoints || 0;

      const mobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
      const isLegacyMobile = mobileUserAgent.test(userAgent);
      const isTouchDevice = "ontouchstart" in window || maxTouchPoints > 1;
      const isPhoneSized = innerWidth <= 768;
      const isTabletSized = innerWidth <= 1366;
      const isModernIPad =
        !isLegacyMobile &&
        isTouchDevice &&
        (platformSignature.includes("mac") || platformSignature.includes("ipad"));
      const isIPadPlatform = platformSignature.includes("ipad");

      setIsMobile(
        isLegacyMobile ||
          isIPadPlatform ||
          (isTouchDevice && (isPhoneSized || isTabletSized)) ||
          isModernIPad
      );
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);
  
  // Drawer state for mobile detail view
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<TimelineCard | null>(null);

  const { t } = useI18n();
  const { user } = useUser();
  const { userSettings } = useUserSettings();
  const { saveCard, savingWord, isCardSaved } = useWordExplanation(userSettings, undefined, user?.id);

  const canAttemptSave = useMemo(
    () => Boolean(user?.id && userSettings?.myVocabularySetting),
    [user?.id, userSettings?.myVocabularySetting]
  );

  const saveDisabledReason = useMemo(() => {
    if (!user?.id) {
      return t("vocabulary.save.loginRequired", "Sign in to save words");
    }
    if (!userSettings?.myVocabularySetting) {
      return t("vocabulary.save.enableSetting", "Enable My Vocabulary in settings to save words");
    }
    return "";
  }, [user?.id, userSettings?.myVocabularySetting, t]);

  const saveLabel = t("common.save", "Save");
  const savedLabel = t("common.saved", "Saved");
  const savingLabel = t("common.saving", "Saving...");

  const buildContextText = useCallback((card?: TimelineCard | null) => {
    if (!card) return undefined;
    const parts: string[] = [];
    if (card.scenarioName) {
      parts.push(`Scenario: ${card.scenarioName}`);
    }
    const contextDescription =
      typeof card.term.context_description === "string" ? card.term.context_description.trim() : "";
    if (contextDescription) {
      parts.push(contextDescription);
    }

    const practicalExampleText =
      card.practicalExample?.english || card.practicalExample?.sentence || "";
    const originalExampleText = card.example?.english || card.example?.sentence || "";
    const exampleText = practicalExampleText || originalExampleText;

    if (exampleText) {
      parts.push(exampleText);
    }

    if (parts.length === 0) {
      return undefined;
    }

    return parts.join(" • ");
  }, []);

  const createTranslationPayload = useCallback((term?: VocabularyTerm | null) => {
    if (!term) return undefined;
    const rawTerm = typeof term.term === "string" ? term.term.trim() : "";
    const chosenExplanation = normalizeChosenExplanationText(term.chosen_explanation);
    const fallbackExplanation = typeof term.explanation === "string" ? term.explanation.trim() : "";
    const translation = chosenExplanation || fallbackExplanation || rawTerm;
    if (!translation) {
      return undefined;
    }
    const explains = chosenExplanation
      ? [chosenExplanation]
      : fallbackExplanation
        ? [fallbackExplanation]
        : undefined;
    return {
      translation,
      explains,
    };
  }, []);

  const handleSaveSelectedCard = useCallback(
    async (event?: React.MouseEvent<HTMLButtonElement>) => {
      event?.preventDefault();
      event?.stopPropagation();

      if (!selectedCard) {
        return;
      }

      const term = selectedCard.term;
      const rawWord = typeof term?.term === "string" ? term.term.trim() : "";
      if (!rawWord) {
        return;
      }

      if (!canAttemptSave) {
        return;
      }

      if (isCardSaved(rawWord)) {
        return;
      }

      const payload = createTranslationPayload(term);
      if (!payload) {
        return;
      }

      try {
        await saveCard(rawWord, {
          contextText: buildContextText(selectedCard),
          youdaoTranslation: payload,
          status: "NEW",
        });
      } catch (error) {
        console.error("Failed to save vocabulary term:", error);
      }
    },
    [buildContextText, canAttemptSave, createTranslationPayload, isCardSaved, saveCard, selectedCard]
  );

  const selectedWord = useMemo(() => {
    const raw = selectedCard?.term?.term;
    return typeof raw === "string" ? raw.trim() : "";
  }, [selectedCard]);

  const lowerSelectedWord = useMemo(() => selectedWord.toLowerCase(), [selectedWord]);

  const isSelectedCardSaving = useMemo(() => {
    if (!selectedWord) return false;
    return Boolean(savingWord[selectedWord] || (lowerSelectedWord ? savingWord[lowerSelectedWord] : false));
  }, [lowerSelectedWord, savingWord, selectedWord]);

  const isSelectedCardSaved = useMemo(() => {
    if (!selectedWord) return false;
    if (isCardSaved(selectedWord)) {
      return true;
    }
    if (lowerSelectedWord && lowerSelectedWord !== selectedWord) {
      return isCardSaved(lowerSelectedWord);
    }
    return false;
  }, [isCardSaved, lowerSelectedWord, selectedWord]);

  const normalizedDisabledReason = saveDisabledReason.trim() ? saveDisabledReason : undefined;

  const saveButtonDisabled =
    !selectedWord || isSelectedCardSaved || isSelectedCardSaving || !canAttemptSave;

  const saveButtonIcon = isSelectedCardSaving ? (
    <Loader2 className="h-4 w-4 animate-spin" />
  ) : isSelectedCardSaved ? (
    <Check className="h-4 w-4" />
  ) : (
    <ClipboardCopy className="h-4 w-4" />
  );

  const saveButtonTitle =
    isSelectedCardSaved || !normalizedDisabledReason || !saveButtonDisabled
      ? isSelectedCardSaved
        ? savedLabel
        : undefined
      : normalizedDisabledReason;

  const baseAccessibleLabel = isSelectedCardSaving ? savingLabel : isSelectedCardSaved ? savedLabel : saveLabel;

  const saveButtonAccessibleLabel =
    saveButtonTitle && saveButtonTitle.trim().length > 0 ? saveButtonTitle : baseAccessibleLabel;

  // 构建时间轴卡片数据
  const timelineCards = useMemo(() => {
    const cards: TimelineCard[] = [];
    let cardId = 0;

    vocabulary.scenarios?.forEach(scenario => {
      scenario.terms?.forEach(term => {
        // 查找original类型的examples
        const originalExamples = term.examples?.filter(example => 
          example.type === 'original' && 
          example.timestamp !== undefined && 
          example.timestamp !== null
        ) || [];
        
        originalExamples.forEach(example => {
          const timestamp = parseTimestampValue(example.timestamp ?? term.timestamp);
          if (timestamp === null || timestamp <= 0) {
            return;
          }

          const colorInfo = getTermColorInfo(term.term, scenario.name);

          const practicalExample = term.examples?.find(ex => ex.type === 'practical') || example;
          const rawEndTimestamp = example.endTimestamp ?? practicalExample?.endTimestamp ?? term.endTimestamp;
          const parsedEndTimestamp = parseTimestampValue(rawEndTimestamp);
          const endTimestamp = parsedEndTimestamp !== null && parsedEndTimestamp > timestamp
            ? parsedEndTimestamp
            : undefined;
          
          cards.push({
            term,
            example,
            practicalExample,
            timestamp,
            endTimestamp,
            scenarioName: scenario.name,
            id: cardId++,
            colorIndex: colorInfo.colorIndex,
            backgroundClass: colorInfo.backgroundClass,
            lightBackgroundClass: colorInfo.lightBackgroundClass,
            darkBackgroundClass: colorInfo.darkBackgroundClass,
            textClass: colorInfo.textClass,
            level: normalizeCefrLevel(term.level),
          });
        });
      });
    });
    
    // 按时间戳排序
    return cards.sort((a, b) => a.timestamp - b.timestamp);
  }, [vocabulary]);

  useEffect(() => {
    if (!timelineCards.length) {
      if (revealedCardIds.size > 0) {
        setRevealedCardIds(new Set());
      }
      previousRevealAllRef.current = revealAllTranslations;
      return;
    }

    if (revealAllTranslations) {
      setRevealedCardIds((prev) => {
        let hasChanges = false;
        const next = new Set(prev);
        for (const card of timelineCards) {
          if (!next.has(card.id)) {
            next.add(card.id);
            hasChanges = true;
          }
        }
        return hasChanges ? next : prev;
      });
    } else if (previousRevealAllRef.current) {
      setRevealedCardIds(new Set());
    }

    previousRevealAllRef.current = revealAllTranslations;
  }, [revealAllTranslations, timelineCards, revealedCardIds]);

  // 根据当前播放时间确定应该显示的卡片
  const getActiveCardIndices = useMemo(() => {
    if (timelineCards.length === 0) return [];
    
    const activeIndices: number[] = [];
    
    for (let i = 0; i < timelineCards.length; i++) {
      const card = timelineCards[i];
      const cardEnd = card.endTimestamp ?? card.timestamp + CARD_POST_GRACE_SECONDS;
      const previewWindowStart = card.timestamp - CARD_PREVIEW_LEAD_SECONDS;
      const displayWindowEnd = cardEnd + CARD_POST_GRACE_SECONDS;

      if (currentTime >= previewWindowStart && currentTime <= displayWindowEnd) {
        activeIndices.push(i);
      }
    }
    
    return activeIndices;
  }, [timelineCards, currentTime]);

  // 获取主要的active card index
  const primaryActiveCardIndex = useMemo(() => {
    if (getActiveCardIndices.length === 0) return -1;
    return getActiveCardIndices.reduce((closest, current) => {
      const closestDiff = Math.abs(timelineCards[closest].timestamp - currentTime);
      const currentDiff = Math.abs(timelineCards[current].timestamp - currentTime);
      return currentDiff < closestDiff ? current : closest;
    });
  }, [getActiveCardIndices, timelineCards, currentTime]);

  // 报告当前active vocabulary terms
  useEffect(() => {
    if (!onActiveVocabularyTermsChange) return;

    const activeEntriesWithIndex = getActiveCardIndices
      .map((index) => {
        const card = timelineCards[index];
        const termName = card?.term?.term;

        if (!card || !termName || !termName.trim()) {
          return null;
        }

        const entry: ActiveVocabularyTermData = {
          term: termName,
          scenarioName: card.scenarioName,
          colorIndex: card.colorIndex,
          backgroundClass: card.backgroundClass,
          lightBackgroundClass: card.lightBackgroundClass,
          darkBackgroundClass: card.darkBackgroundClass,
          textClass: card.textClass,
          level: card.level,
        };

        return { entry, timelineIndex: index };
      })
      .filter((item): item is { entry: ActiveVocabularyTermData; timelineIndex: number } => Boolean(item));

    if (!activeEntriesWithIndex.length) {
      onActiveVocabularyTermsChange([]);
      return;
    }

    const orderedEntries = primaryActiveCardIndex >= 0
      ? [...activeEntriesWithIndex].sort((a, b) => {
          if (a.timelineIndex === primaryActiveCardIndex) return -1;
          if (b.timelineIndex === primaryActiveCardIndex) return 1;
          return a.timelineIndex - b.timelineIndex;
        })
      : activeEntriesWithIndex;

    const uniqueEntries = new Map<string, ActiveVocabularyTermData>();

    for (const { entry } of orderedEntries) {
      const key = createVocabularyTermKey(entry.term, entry.scenarioName);
      if (!key) {
        continue;
      }
      if (!uniqueEntries.has(key)) {
        uniqueEntries.set(key, entry);
      }
    }

    onActiveVocabularyTermsChange(Array.from(uniqueEntries.values()));
  }, [
    getActiveCardIndices,
    primaryActiveCardIndex,
    timelineCards,
    onActiveVocabularyTermsChange,
  ]);

  // Handle card click for mobile detail view
  // const handleCardClick = (card: TimelineCard) => {
  //   setSelectedCard(card);
  //   setIsDrawerOpen(true);
  // };

  // Desktop scroll functions (keeping existing functionality)
  const scrollToCard = useCallback((index: number) => {
    if (!containerRef.current || index < 0 || index >= timelineCards.length) return;
                                                
    const container = containerRef.current;
    const cards = container.querySelectorAll('[data-immersive-card]');
    if (!cards.length) return;

    const targetCard = cards[index] as HTMLElement | undefined;
    if (!targetCard) return;

    const cardOffsetLeft = targetCard.offsetLeft;
    const cardWidth = targetCard.offsetWidth;
    const containerCenter = container.clientWidth / 2;
    const cardCenter = cardOffsetLeft + cardWidth / 2;

    const scrollPosition = cardCenter - containerCenter;
    const maxScroll = container.scrollWidth - container.clientWidth;
    const clampedScrollPosition = Math.max(0, Math.min(maxScroll, scrollPosition));

    container.scrollTo({
      left: clampedScrollPosition,
      behavior: 'smooth',
    });
  }, [timelineCards]);

  const scroll = (direction: 'left' | 'right') => {
    if (!containerRef.current) return;
    
    const container = containerRef.current;
    const scrollAmount = 336;
    const currentScroll = container.scrollLeft;
    const newScroll = direction === 'left' 
      ? Math.max(0, currentScroll - scrollAmount)
      : Math.min(container.scrollWidth - container.clientWidth, currentScroll + scrollAmount);
    
    container.scrollTo({
      left: newScroll,
      behavior: 'smooth'
    });
  };

  const updateScrollButtons = () => {
    if (!containerRef.current) return;
    
    const container = containerRef.current;
    setCanScrollLeft(container.scrollLeft > 0);
    setCanScrollRight(container.scrollLeft < container.scrollWidth - container.clientWidth);
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('scroll', updateScrollButtons);
    updateScrollButtons();

    return () => container.removeEventListener('scroll', updateScrollButtons);
  }, [timelineCards]);

  useEffect(() => {
    if (isMobile || primaryActiveCardIndex < 0) return;

    const frame = requestAnimationFrame(() => {
      scrollToCard(primaryActiveCardIndex);
    });

    return () => cancelAnimationFrame(frame);
  }, [isMobile, primaryActiveCardIndex, scrollToCard]);

  if (!timelineCards.length) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>No vocabulary cards with timestamps available</p>
      </div>
    );
  }

  // Render mobile card stack view
  if (isMobile) {
    return (
      <div className="w-full">
        <VocabularyCardStack
          items={timelineCards}
          currentTime={currentTime}
          onTimestampClick={onTimestampClick}
          activeCardIndex={primaryActiveCardIndex}
          setSelectedCard={setSelectedCard}
          setIsDrawerOpen={setIsDrawerOpen}
          isTranslationRevealed={isTranslationRevealed}
          onRevealTranslation={revealTranslation}
        />

        {/* Mobile Drawer for detailed view */}
        <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
          <DrawerContent className="max-h-[85vh] rounded-t-3xl border border-border">
            {selectedCard && (
              <>
                <DrawerHeader className="text-left">
                  <DrawerTitle className="font-sans flex items-center space-x-2 text-xl">
                    <span>{selectedCard.term.term.charAt(0).toUpperCase() + selectedCard.term.term.slice(1)}</span>
                    <SpeakerIcon 
                      lang="en" 
                      text={selectedCard.term.term}
                      size={12}
                    />
                  </DrawerTitle>
                  <DrawerDescription className="text-sm text-muted-foreground">
                    {selectedCard.scenarioName}
                  </DrawerDescription>
                </DrawerHeader>

                <div className="px-4 pb-4 overflow-y-auto flex-1">
                  <div className="space-y-4">
                    {/* Full explanation */}
                    <div className="bg-muted/30 rounded-lg p-3">
                      <TranslationReveal
                        revealed={isTranslationRevealed(selectedCard.id)}
                        onReveal={() => revealTranslation(selectedCard.id)}
                        className="text-sm text-foreground leading-relaxed"
                        overlayLabel="Double click to reveal translation"
                      >
                        {selectedCard.term.explanation}
                      </TranslationReveal>
                    </div>
                    
                    {/* Context description */}
                    {selectedCard.term.context_description && (
                      <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
                        <div className="text-sm text-muted-foreground">
                          <strong className="text-foreground">使用场景：</strong>
                          {selectedCard.term.context_description}
                        </div>
                      </div>
                    )}

                    {/* Example */}
                    {selectedCard.practicalExample && (
                      <div className="bg-muted/30 rounded-lg p-3 space-y-2">
                        <div className="text-sm text-foreground font-medium">
                          Example:
                        </div>
                        <div className="text-sm text-foreground">
                          {selectedCard.practicalExample.english || selectedCard.practicalExample.sentence}
                        </div>
                        {selectedCard.practicalExample.translation && (
                          <TranslationReveal
                            revealed={isTranslationRevealed(selectedCard.id)}
                            onReveal={() => revealTranslation(selectedCard.id)}
                            className="text-sm text-muted-foreground border-l-2 border-primary/30 pl-3"
                            overlayLabel="Double click to reveal translation"
                          >
                            {selectedCard.practicalExample.translation}
                          </TranslationReveal>
                        )}
                      </div>
                    )}

                    {/* Timestamp button */}
                    {(onTimestampClick || selectedWord) && (
                      <div className="flex w-full items-center gap-2">
                        {onTimestampClick && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              onTimestampClick(selectedCard.timestamp, selectedCard.endTimestamp);
                              setIsDrawerOpen(false);
                            }}
                            className="flex-1 rounded-xl"
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Jump to {formatTimestamp(selectedCard.timestamp)}
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleSaveSelectedCard}
                          disabled={saveButtonDisabled}
                          title={saveButtonTitle || saveButtonAccessibleLabel}
                          aria-label={saveButtonAccessibleLabel}
                          className="rounded-xl px-3"
                        >
                          {saveButtonIcon}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </DrawerContent>
        </Drawer>
      </div>
    );
  }

  // Render desktop horizontal scroll view (existing functionality)
  return (
    <div className="relative w-full">
      {/* Scroll Container */}
      <div className="relative">
        {/* Left Scroll Button */}
        <AnimatePresence>
          {canScrollLeft && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10"
            >
              <Button
                variant="outline"
                size="sm"
                onClick={() => scroll('left')}
                className="h-8 w-8 p-0 rounded-full bg-background/90 backdrop-blur-sm shadow-lg border-border/50"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Right Scroll Button */}
        <AnimatePresence>
          {canScrollRight && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10"
            >
              <Button
                variant="outline"
                size="sm"
                onClick={() => scroll('right')}
                className="h-8 w-8 p-0 rounded-full bg-background/90 backdrop-blur-sm shadow-lg border-border/50"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Cards Container */}
        <div
          ref={containerRef}
          className="flex overflow-x-auto scroll-smooth gap-4 pt-1 px-4 pb-4 scrollbar-hide"
        >
          <AnimatePresence initial={false}>
          {timelineCards.map((card, index) => {
            const cardEnd = card.endTimestamp ?? card.timestamp + CARD_POST_GRACE_SECONDS;
            const isHighlighted = getActiveCardIndices.includes(index);
            const isCurrentlyPlaying = currentTime >= card.timestamp && currentTime <= cardEnd;
            const isPast = currentTime > cardEnd;
            const timeUntilStart = card.timestamp - currentTime;
            const isCountdownPhase = timeUntilStart > 0 && timeUntilStart <= CARD_PREVIEW_LEAD_SECONDS;
            const shouldUseHighlightColor = !isCountdownPhase && (isCurrentlyPlaying || isPast);
            const showAccentBackground = !shouldUseHighlightColor;
            const cardBackgroundClass = showAccentBackground ? card.backgroundClass : 'bg-transparent';
            const termColorClass = shouldUseHighlightColor ? card.textClass : 'text-foreground';
            const translationRevealed = isTranslationRevealed(card.id);
            
            return (
              <motion.div
                key={`${card.term.term}-${card.timestamp}-${index}`}
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ 
                  scale: isHighlighted ? 1.02 : 0.95,
                  opacity: isHighlighted ? 1 : (isPast ? 0.6 : 0.8)
                }}
                exit={{ opacity: 0, scale: 0.85, transition: { duration: 0.25 } }}
                transition={{ duration: 0.3 }}
                className="flex-shrink-0"
                data-immersive-card
              >
                <HoverCard openDelay={300}>
                  <HoverCardTrigger asChild>
                    <div
                      className={cn(
                        'w-80 backdrop-blur-xl rounded-3xl transition-all duration-300 cursor-pointer border',
                        // cardBackgroundClass,
                        isCurrentlyPlaying 
                          ? `border-primary/50 shadow-lg shadow-primary/10 ${cardBackgroundClass}`
                          : isPast 
                          ? 'border-border'
                          : 'border-border'
                      )}
                    >
                      <div className="p-2">
                        {/* Card Header */}
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <h4 className={cn(
                                'pl-2 font-sans font-semibold text-lg line-clamp-1 transition-colors duration-200',
                                termColorClass
                              )}>
                                {card.term.term.charAt(0).toUpperCase() + card.term.term.slice(1)}
                              </h4>
                              <SpeakerIcon 
                                lang="en" 
                                text={card.term.term}
                                size={16}
                              />
                            </div>
                          </div>
                          {onTimestampClick && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                onTimestampClick(card.timestamp, card.endTimestamp);
                              }}
                              className="h-6 pb-1 px-2 border-1 text-xs rounded-full gap-1 leading-none"
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              {formatTimestamp(card.timestamp)}
                            </Button>
                          )}
                        </div>

                        {/* Explanation - Truncated */}
                        <TranslationReveal
                          revealed={translationRevealed}
                          onReveal={() => revealTranslation(card.id)}
                          className="text-sm text-muted-foreground/70 line-clamp-1"
                        >
                          {card.term.explanation}
                        </TranslationReveal>

                        {/* Status Indicator */}
                        <div className="flex pl-2 items-center justify-between text-xs mt-1.5">
                          <div className={`flex items-center space-x-1 ${
                            isCurrentlyPlaying ? 'text-background' : 
                            isPast ? 'text-muted-foreground' : 
                            'text-muted-foreground'
                          }`}>
                            <div className={`w-2 h-2 rounded-full ${
                              isCurrentlyPlaying ? 'bg-background animate-pulse' : 
                              isPast ? 'bg-muted-foreground' : 
                              'bg-muted-foreground/50'
                            }`} />
                            <span>
                              {isCurrentlyPlaying ? 'CURRENT' : 
                               isPast ? 'Completed' : 
                               'Upcoming'}
                            </span>
                          </div>
                          <div className="text-muted-foreground">
                            {isCurrentlyPlaying ? (
                              <span className="text-rose-400 font-bold font-sans text-base">NOW</span>
                            ) : card.timestamp > currentTime ? (
                              <>
                                In <span className={`${
                                  Math.ceil(card.timestamp - currentTime) <= 3
                                    ? 'text-background font-bold text-lg'
                                    : ''
                                 }`}>
                                  {Math.ceil(card.timestamp - currentTime)}s
                                </span>
                              </>
                            ) : 
                            `${Math.ceil(Math.max(0, currentTime - cardEnd))}s ago`}
                          </div>
                        </div>
                      </div>
                    </div>
                  </HoverCardTrigger>
                  
                  <HoverCardContent 
                    className="w-[480px] p-3 z-50 rounded-3xl border-border/60" 
                    side="top" 
                    align="center"
                    sideOffset={8}
                  >
                    <div className="space-y-3">
                      {/* Header */}
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-0.5">
                            <h4 className="font-semibold text-lg text-foreground">
                              {card.term.term}
                            </h4>
                            <SpeakerIcon 
                              lang="en" 
                              text={card.term.term}
                              size={12}
                            />
                          </div>
                          <div className="text-[11px] text-muted-foreground/90">{card.scenarioName}</div>
                        </div>
                        {onTimestampClick && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onTimestampClick(card.timestamp, card.endTimestamp)}
                            className="h-6 px-2 py-0 text-[11px] rounded-full border-[1px] gap-1 leading-none"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            {formatTimestamp(card.timestamp)}
                          </Button>
                        )}
                      </div>

                      {/* Full Explanation */}
                      <TranslationReveal
                        revealed={translationRevealed}
                        onReveal={() => revealTranslation(card.id)}
                        className="text-[13px] text-foreground leading-snug"
                        overlayLabel="Double click to reveal translation"
                      >
                        {card.term.explanation}
                      </TranslationReveal>

                      {/* Context Description */}
                      {card.term.context_description && (
                        <div className="bg-muted/40 rounded-md p-2.5">
                          <div className="text-[12px] text-muted-foreground">
                            <span className="text-foreground font-medium mr-1">使用场景</span>
                            {card.term.context_description}
                          </div>
                        </div>
                      )}

                      {/* Example */}
                      <div className="rounded-md p-2.5 border border-border/60 bg-background/70">
                        <div className="text-[12px] text-muted-foreground mb-1">Example</div>
                        <div className="text-[13px] text-foreground">
                          {card.practicalExample.english || card.practicalExample.sentence}
                        </div>
                        {card.practicalExample.translation && (
                          <TranslationReveal
                            revealed={translationRevealed}
                            onReveal={() => revealTranslation(card.id)}
                            className="text-[12px] text-muted-foreground mt-1"
                          >
                            {card.practicalExample.translation}
                          </TranslationReveal>
                        )}
                      </div>
                    </div>
                  </HoverCardContent>
                </HoverCard>
              </motion.div>
            );
          })}
          </AnimatePresence>
        </div>
      </div>

      <style jsx global>{`
        .scrollbar-hide {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
}
